import { createSelector } from 'reselect';
import {
    transformTags,
    transformSelectDate,
    transformContributors,
} from '../Dashboard/transformers';
import {
    transformChallenge,
    transformConnectedChallenges,
} from '../Campaigns/transformer';
import { transformMapDataImpact as transformMapData } from '../../map/transformers';
import { transformSegments } from './transformers';
import { numberWithCommas } from '../../common/utils';

export const challengeIdState = (state) => state.campaign.challengeId || null;

export const challengeState = (state) => state.campaign.challenge || {};

export const challengeLoadingState = (state) =>
    state.campaign.challengeRequested || null;

export const connectedChallengesState = (state) =>
    state.campaign.connectedChallenges || [];

export const parentChallengeState = (state) =>
    state.campaign.parentChallenge || null;

export const connectedChallengesLoadingState = (state) =>
    state.campaign.connectedChallengesLoading || [];

export const segmentIdState = (state) => state.campaign.segmentId || null;

export const getTaxonomyState = (state) => state.campaign.taxonomy || '';

export const getSegmentTaxonomyState = (state) =>
    state.campaign.segmentTaxonomy || '';

export const getSelectDateState = (state) => state.campaign.date || '';

export const getCustomDateState = (state) => state.campaign.customDate || {};

export const getTopTagsState = (state) =>
    state.campaign.topTags || { mlTags: [], userTags: [] };

export const getChallengeSegmentTags = (state) => state.campaign.segmentTags;

export const getSegmentTagsState = createSelector(
    getChallengeSegmentTags,
    (tagsState) => tagsState.topTags || { mlTags: [], userTags: [] },
);

export const getTopContributorsState = (state) =>
    state.campaign.topContributors || [];

export const getContributorsLoadingState = (state) =>
    state.campaign.contributorsLoading || false;

export const getSegmentTagsLoadingState = (state) =>
    state.campaign.segmentTagsLoading || false;

export const getSegmentsLoadingState = (state) =>
    state.campaign.segmentsLoading || false;

export const getTagsLoadingState = (state) =>
    state.campaign.tagsLoading || false;

export const getTotalTagsState = (state) => state.campaign.totalTags || 0;

export const getSegmentTagsTotalState = createSelector(
    getChallengeSegmentTags,
    (tagsState) => tagsState.totalTags || 0,
);

export const getSegmentsState = (state) => state.campaign.segments || [];

export const getTopContributors = createSelector(
    getTopContributorsState,
    (topContributors) => transformContributors(topContributors),
);

export const getContributorsLoading = createSelector(
    getContributorsLoadingState,
    (loading) => loading,
);

export const getChallengeId = createSelector(
    challengeIdState,
    (challengeId) => challengeId,
);

export const getChallenge = createSelector(challengeState, (challenge) =>
    transformChallenge(challenge),
);

export const getChallengeLoading = createSelector(
    challengeLoadingState,
    (loading) => loading,
);

export const getConnectedChallenges = createSelector(
    connectedChallengesState,
    (challenges) => transformConnectedChallenges(challenges),
);

export const getParentChallenge = createSelector(
    parentChallengeState,
    (challenge) => challenge,
);

export const getConnectedChallengesLoading = createSelector(
    connectedChallengesLoadingState,
    (loading) => loading,
);

export const getSegmentId = createSelector(
    segmentIdState,
    (segmentId) => segmentId,
);

export const getSegmentTaxonomy = createSelector(
    getSegmentTaxonomyState,
    (taxonomy) => taxonomy,
);

export const getTaxonomy = createSelector(
    getTaxonomyState,
    (taxonomy) => taxonomy,
);

export const getTopTags = createSelector(getTopTagsState, (topTags) =>
    transformTags(topTags.userTags, 'orange'),
);

export const getMlTags = createSelector(getTopTagsState, (topTags) =>
    transformTags(topTags.mlTags, 'blue'),
);

export const getSegmentTags = createSelector(getSegmentTagsState, (tags) =>
    transformTags(tags.userTags, 'orange'),
);

export const getSegmentMLTags = createSelector(getSegmentTagsState, (tags) =>
    transformTags(tags.mlTags, 'blue'),
);

export const getSegments = createSelector(getSegmentsState, (segments) =>
    transformSegments(segments),
);

export const getTagsLoading = createSelector(
    getTagsLoadingState,
    (loading) => loading,
);

export const getSegmentTagsLoading = createSelector(
    getSegmentTagsLoadingState,
    (loading) => loading,
);

export const getSegmentsLoading = createSelector(
    getSegmentsLoadingState,
    (loading) => loading,
);

export const getTotalTags = createSelector(getTotalTagsState, (totalTags) =>
    numberWithCommas(totalTags),
);

export const getSegmentTotalTags = createSelector(
    getSegmentTagsTotalState,
    (totalTags) => numberWithCommas(totalTags),
);

export const getSelectDate = createSelector(
    getSelectDateState,
    getCustomDateState,
    (date, customDate) => transformSelectDate(date, customDate),
);

// map state

const filterState = (state) => state.campaign.selectedFilter || '';
const mapData = (state) => state.campaign.impactLitters || [];
const litterRequested = (state) => state.campaign.litterRequested || false;

export const getSelectedFilter = createSelector(
    filterState,
    (filterState) => filterState,
);

export const isMapFilterRequested = createSelector(
    litterRequested,
    (litterRequested) => litterRequested,
);

export const getImpactLitters = createSelector(
    [mapData, filterState],
    (mapData, filterState) => transformMapData(mapData, filterState),
);

export const getRequestedSegmentsLimit = (state) =>
    state.campaign.segmentsLimit || false;

export const getChallengeTotalMlTags = (state) =>
    state.campaign.totalMlTags || '';

export const getChallengeTotalDistilledTags = (state) =>
    state.campaign.totalDistilledTags || '';
